import { ArrowOutward, Close } from '@mui/icons-material';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import { Box, Button, Drawer, DrawerProps, IconButton, List, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { Fragment, ReactNode, useState } from 'react';
import { TagCell } from '../../../../common/components/Cells/TagCell';

export type ExportDrawerChips = {
  title: string;
  chips: string[];
};

export type ExportDrawerProps = Omit<DrawerProps, 'children'> & {
  title: string;
  triggerLabel?: string;
  additionalContent?: ReactNode;

  disabled?: boolean;

  chips: ExportDrawerChips[];
  selectors: ReactNode[];
  actionButtonProps: LoadingButtonProps;
};

export const ExportsDrawer = ({ title, triggerLabel, additionalContent, disabled, chips, selectors, actionButtonProps, ...rest }: ExportDrawerProps) => {
  const [open, setOpen] = useState(false);

  const onOpen = () => {
    setOpen(true);
    setTimeout(() => {
      const firstSelector = document.querySelectorAll('[data-role=export-format-selector]')[0] as HTMLButtonElement;
      firstSelector.focus();
    });
  };

  const onClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button data-role="export-drawer-trigger" variant="outlined" startIcon={<ArrowOutward />} onClick={onOpen} disabled={disabled}>
        {triggerLabel ?? t('export')}
      </Button>
      <Drawer
        {...rest}
        data-role="export-drawer-container"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { p: 4, width: '30rem', backgroundColor: 'grey.100' } }}
        ModalProps={{ keepMounted: true }}
        anchor="right"
        role="complementary"
      >
        <Stack>
          <Stack direction="row" spacing={1} sx={{ pb: 1, alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body1" component="span">
              {t('export')}
            </Typography>
            <IconButton title={t('close')} size="small" tabIndex={0} onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
          <Typography component="p" variant="h4" sx={{ pb: 2 }}>
            {title}
          </Typography>
        </Stack>
        <Stack spacing={2}>
          {chips.map((c, index) => (
            <Box key={c.title} data-role={`export-drawer-chips-${index}`}>
              <Typography variant="overline">{c.title}</Typography>
              <TagCell tags={c.chips} />
            </Box>
          ))}
          {additionalContent}
          <List component="nav">
            {selectors.map((s, index) => (
              <Fragment key={index}>{s}</Fragment>
            ))}
          </List>
        </Stack>
        <Box sx={{ flexGrow: 1, mb: 2 }} />
        <LoadingButton {...actionButtonProps} variant="outlined" fullWidth />
      </Drawer>
    </>
  );
};
